/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { focusBoxShadow, VFlow } from 'bold-ui'
import { AccordionAnimation } from 'components/accordion/AccordionAnimation'
import { PageLoading } from 'components/loading'
import theme from 'config/theme'
import { format } from 'date-fns'
import { useEvolucoesProblemaModalLazyQuery } from 'graphql/hooks.generated'
import { useState } from 'react'
import { Accordion, AccordionItem, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion'
import { Problema } from 'view/atendimentos/detail/soap/aside/types/ProblemaModel'

import convertEvolucaoProblemaToModel from '../../../soap/aside/lista-problemas/convertEvolucaoProblemaToModel'
import { BodyProblemaModal } from './BodyProblemaModal'
import { HeaderProblemaModal } from './HeaderProblemaModal'

interface AccordionProblemasModalProps {
  problemas: Problema[]
  dataNascimentoCidadao: LocalDate
  filtro?: string
}

export function AccordionProblemasModal(props: AccordionProblemasModalProps) {
  const { problemas, dataNascimentoCidadao, filtro } = props
  const [problemaLoadingEvolucoes, setProblemaLoadingEvolucoes] = useState<string>()

  const [executeQuery, { data, loading }] = useEvolucoesProblemaModalLazyQuery({
    fetchPolicy: 'cache-and-network',
  })

  const problemaQueryResult = !isNaN(+problemaLoadingEvolucoes) ? data?.problema : null

  const handleProblemaClick = (problemaUUID: string[]) => {
    const problemaId = problemaUUID?.join()
    if (!!problemaId) {
      setProblemaLoadingEvolucoes(problemaId)
      !isNaN(+problemaId) && executeQuery({ variables: { problemaId: problemaId } })
    }
  }

  const evolucoes = [
    ...(problemas.find((problema) => problema.id === problemaLoadingEvolucoes)?.evolucoes ?? []),
    ...(problemaQueryResult?.evolucoes.map((evo) => convertEvolucaoProblemaToModel(evo)) ?? []),
  ]

  const { accordionItemButtonStyles, accordionItemPanelStyles, accortionItemStyles } = styles

  return (
    <Accordion allowZeroExpanded={true} onChange={handleProblemaClick}>
      <VFlow vSpacing={0.5}>
        {problemas.map((problema) => (
          <AccordionItem uuid={problema.id} key={problema.id} css={accortionItemStyles}>
            <AccordionAnimation>
              <AccordionItemButton css={accordionItemButtonStyles}>
                <HeaderProblemaModal
                  problema={problema}
                  dataNascimentoCidadao={dataNascimentoCidadao}
                  highlight={filtro}
                />
              </AccordionItemButton>
              <AccordionItemPanel css={accordionItemPanelStyles}>
                {loading ? (
                  <PageLoading message='Carregando evoluções...' />
                ) : (
                  evolucoes?.map((evolucao, index) => {
                    return (
                      <div
                        key={index}
                        css={css`
                          padding: 0.5rem 1rem;
                        `}
                      >
                        <BodyProblemaModal evolucaoProblema={evolucao} />
                      </div>
                    )
                  })
                )}
              </AccordionItemPanel>
            </AccordionAnimation>
          </AccordionItem>
        ))}
      </VFlow>
    </Accordion>
  )
}

AccordionProblemasModal.defaultProps = {
  changed: true,
}

export const formatDate = (value: number | string): string => (value ? format(new Date(value), 'DD/MM/YYYY') : '-')

const styles = {
  accortionItemStyles: css`
    border: 1px solid ${theme.pallete.gray.c60};
    border-radius: ${theme.radius.input}px;
    background: ${theme.pallete.gray.c90};
    &:focus-within {
      outline: none;
      box-shadow: ${focusBoxShadow(theme, 'primary')};
      transition-property: box-shadow;
      transition-duration: 0.2s;
    }
  `,

  accordionItemButtonStyles: css`
    padding: 0.5rem 1rem;
    cursor: pointer;
    &:hover {
      border-color: ${theme.pallete.gray.c50};
      background-color: ${theme.pallete.gray.c80};
    }
    &:focus {
      outline: none;
      box-shadow: none;
    }
    &[aria-expanded='true'] svg[name='accordionAngleIcon'] {
      transform: rotate(180deg);
      transition: 0.4s;
    }
    &[aria-expanded='false'] svg[name='accordionAngleIcon'] {
      transform: rotate(0deg);
      transition: 0.4s;
    }
  `,
  accordionItemPanelStyles: css`
    flex: 1;
    background-color: ${theme.pallete.surface.main};
    max-height: 20rem;
    overflow: auto;
    & > :not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c80};
    }
  `,
}
